import { FormEvent, useEffect, useRef, useState, type InputHTMLAttributes } from 'react';
import { X } from 'lucide-react';

import { AdminCompanyCreateResponse } from '../../services/api';
import { useAdminCompanyMutations } from '../../hooks/useApi';
import { getMutationErrorMessage } from '../../lib/apiErrors';
import { useTouchedFields } from '../../lib/touchedFormValidation';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { dialogFooterClassName } from '../ui/dialog-frame';
import { Field, FormErrorAlert, useFocusFirstInvalid } from '../ui/form-field';
import { ModalOverlay, modalSheetClassName } from '../ui/modal-overlay';
import { useModalDialog } from '../ui/use-modal-dialog';
import { formFieldGroupSurfaceClassName } from '../ui/formPrimitives';
import { Input } from '../ui/input';
import { dialogTitleClassName } from '../ui/typography';
import {
    mergeCompanyUserlyticsSettings,
    readCompanyUserlyticsSettings,
    type CompanyUserlyticsSettings,
} from './companyUserlyticsCustomizations';

const DEFAULT_INDUSTRY_ID = 1;
const EMPTY_USERLYTICS_SETTINGS: CompanyUserlyticsSettings = {
    enabled: false,
    email: '',
    domain: '',
    teamId: '',
    projectId: '',
};

interface AdminCreateCompanyModalProps {
    open: boolean;
    onClose: () => void;
    onCreated?: (result: AdminCompanyCreateResponse) => void;
}

interface AdminEditCompanyModalProps {
    company: {
        companyId: number;
        name: string;
        isActive: boolean;
        customizations?: Record<string, unknown>;
    } | null;
    open: boolean;
    onClose: () => void;
    onSaved?: (result: AdminCompanyCreateResponse) => void;
}

interface CompanyFormModalProps {
    company?: AdminEditCompanyModalProps['company'];
    mode: 'create' | 'edit';
    open: boolean;
    onClose: () => void;
    onSubmitted?: (result: AdminCompanyCreateResponse) => void;
}

const COMPANY_MUTATION_ERROR_BY_MODE: Record<CompanyFormModalProps['mode'], string> = {
    create: 'Could not create company. Try again.',
    edit: 'Could not save company. Try again.',
};

type CompanyFieldName = 'name' | 'userlytics';
const COMPANY_FIELDS: readonly CompanyFieldName[] = ['name', 'userlytics'];

function getCompanyCustomizations(
    baseCustomizations: Record<string, unknown>,
    hasPulseAccess: boolean,
    hasProAccess: boolean,
    userlytics: CompanyUserlyticsSettings,
): Record<string, unknown> {
    const customizations = mergeCompanyUserlyticsSettings(baseCustomizations, userlytics);

    if (hasPulseAccess) {
        const automatedInsights = customizations.automatedInsights;
        if (
            !automatedInsights ||
            typeof automatedInsights !== 'object' ||
            !('allowedUserIds' in automatedInsights)
        ) {
            delete customizations.automatedInsights;
        }
    } else {
        customizations.automatedInsights = { enabled: false };
    }

    if (hasProAccess) {
        delete customizations.core;
    } else {
        customizations.core = { enabled: false };
    }

    return customizations;
}

function derivePulseAccess(customizations: Record<string, unknown>): boolean {
    const automatedInsights = customizations.automatedInsights as { enabled?: boolean } | undefined;
    return automatedInsights?.enabled === true || !('automatedInsights' in customizations);
}

function deriveProAccess(customizations: Record<string, unknown>): boolean {
    const core = customizations.core as { enabled?: boolean } | undefined;
    return core?.enabled === true || !('core' in customizations);
}

export function AdminCreateCompanyModal({
    open,
    onClose,
    onCreated,
}: AdminCreateCompanyModalProps) {
    return <CompanyFormModal mode="create" open={open} onClose={onClose} onSubmitted={onCreated} />;
}

export function AdminEditCompanyModal({
    company,
    open,
    onClose,
    onSaved,
}: AdminEditCompanyModalProps) {
    return (
        <CompanyFormModal
            company={company}
            mode="edit"
            open={open}
            onClose={onClose}
            onSubmitted={onSaved}
        />
    );
}

type UserlyticsTextKey = 'email' | 'domain' | 'teamId' | 'projectId';
type UserlyticsMissing = Record<UserlyticsTextKey, boolean>;

const COMPANY_SUBMIT_LABELS: Record<
    CompanyFormModalProps['mode'],
    { idle: string; pending: string }
> = {
    create: { idle: 'Create company', pending: 'Creating…' },
    edit: { idle: 'Save company', pending: 'Saving…' },
};

const USERLYTICS_TEXT_FIELDS: ReadonlyArray<{
    key: UserlyticsTextKey;
    label: string;
    placeholder: string;
    inputProps: InputHTMLAttributes<HTMLInputElement>;
}> = [
    {
        key: 'email',
        label: 'Userlytics email',
        placeholder: 'research@example.com',
        inputProps: { type: 'email' },
    },
    {
        key: 'domain',
        label: 'Userlytics domain',
        placeholder: 'example.wevo.ai',
        inputProps: { inputMode: 'url', autoCapitalize: 'none', spellCheck: false },
    },
    {
        key: 'teamId',
        label: 'Userlytics team ID',
        placeholder: '7tv40kuw',
        inputProps: { autoCapitalize: 'none', spellCheck: false },
    },
    {
        key: 'projectId',
        label: 'Userlytics project ID',
        placeholder: 'p12dwkeh',
        inputProps: { autoCapitalize: 'none', spellCheck: false },
    },
];

function getCompanyFormErrors(name: string, userlytics: CompanyUserlyticsSettings) {
    const userlyticsMissing: UserlyticsMissing = {
        email: !userlytics.email.trim(),
        domain: !userlytics.domain.trim(),
        teamId: !userlytics.teamId.trim(),
        projectId: !userlytics.projectId.trim(),
    };
    const anyUserlyticsMissing =
        userlytics.enabled && Object.values(userlyticsMissing).some(Boolean);
    const fieldErrors: Partial<Record<CompanyFieldName, string>> = {
        name: !name.trim() ? 'Company name is required.' : undefined,
        userlytics: anyUserlyticsMissing
            ? 'Userlytics email, domain, team ID, and project ID are required.'
            : undefined,
    };
    return { userlyticsMissing, fieldErrors };
}

function UserlyticsFieldGroup({
    userlytics,
    missing,
    error,
    onChange,
}: {
    userlytics: CompanyUserlyticsSettings;
    missing: UserlyticsMissing;
    error: string | undefined;
    onChange: (key: UserlyticsTextKey, value: string) => void;
}) {
    return (
        <Field
            as="group"
            label="Userlytics access"
            legendClassName="sr-only"
            error={error}
            className={`space-y-4 ${formFieldGroupSurfaceClassName}`}
        >
            {USERLYTICS_TEXT_FIELDS.map((field) => (
                <Field
                    key={field.key}
                    label={field.label}
                    required
                    invalid={Boolean(error && missing[field.key])}
                >
                    <Input
                        {...field.inputProps}
                        autoComplete="off"
                        value={userlytics[field.key]}
                        onChange={(event) => onChange(field.key, event.target.value)}
                        placeholder={field.placeholder}
                    />
                </Field>
            ))}
        </Field>
    );
}

function CompanyFormModal({ company, mode, open, onClose, onSubmitted }: CompanyFormModalProps) {
    const { createCompany, updateCompany } = useAdminCompanyMutations();
    const [name, setName] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [hasPulseAccess, setHasPulseAccess] = useState(false);
    const [hasProAccess, setHasProAccess] = useState(false);
    const [userlytics, setUserlytics] =
        useState<CompanyUserlyticsSettings>(EMPTY_USERLYTICS_SETTINGS);
    const [error, setError] = useState<string | null>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const { touch, touchAll, resetTouched, fieldError } = useTouchedFields<CompanyFieldName>();
    const focusFirstInvalid = useFocusFirstInvalid(formRef);
    const { userlyticsMissing, fieldErrors } = getCompanyFormErrors(name, userlytics);
    const visibleUserlyticsError = fieldError('userlytics', fieldErrors.userlytics);

    useEffect(() => {
        if (!open) return;
        const customizations = company?.customizations ?? {};
        setName(company?.name ?? '');
        setIsActive(company?.isActive ?? true);
        setHasPulseAccess(company ? derivePulseAccess(customizations) : false);
        setHasProAccess(company ? deriveProAccess(customizations) : false);
        setUserlytics(readCompanyUserlyticsSettings(customizations));
        setError(null);
        resetTouched();
    }, [company, open, resetTouched]);

    const { titleId, panelProps } = useModalDialog({ open, onClose });
    if (!open) return null;

    const isEdit = mode === 'edit';
    const isPending = isEdit ? updateCompany.isPending : createCompany.isPending;

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setError(null);
        touchAll(COMPANY_FIELDS);

        const trimmedName = name.trim();

        const normalizedUserlytics = {
            ...userlytics,
            email: userlytics.email.trim(),
            domain: userlytics.domain.trim(),
            teamId: userlytics.teamId.trim(),
            projectId: userlytics.projectId.trim(),
        };
        if (Object.values(fieldErrors).some(Boolean)) {
            focusFirstInvalid();
            return;
        }

        try {
            const customizations = getCompanyCustomizations(
                company?.customizations ?? {},
                hasPulseAccess,
                hasProAccess,
                normalizedUserlytics,
            );
            const result =
                isEdit && company
                    ? await updateCompany.mutateAsync({
                          companyId: company.companyId,
                          request: {
                              name: trimmedName,
                              isActive,
                              customizations,
                          },
                      })
                    : await createCompany.mutateAsync({
                          name: trimmedName,
                          industryId: DEFAULT_INDUSTRY_ID,
                          isActive,
                          customizations,
                      });
            onSubmitted?.(result);
            onClose();
        } catch (err) {
            setError(getMutationErrorMessage(err, COMPANY_MUTATION_ERROR_BY_MODE[mode]));
        }
    };

    return (
        <ModalOverlay>
            <div
                {...panelProps}
                className={cn(
                    modalSheetClassName,
                    'flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col',
                )}
            >
                <div className="flex shrink-0 items-start justify-between gap-4 border-b p-5">
                    <div>
                        <h2 id={titleId} className={dialogTitleClassName}>
                            {isEdit ? 'Edit company' : 'Create company'}
                        </h2>
                        <p className="mt-1 text-sm text-neutral-500">
                            {isEdit
                                ? 'Update company profile and product access.'
                                : 'Creates a company with the selected product access.'}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close dialog"
                        className="-m-1 flex size-9 shrink-0 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                    >
                        <X className="size-5" aria-hidden />
                    </button>
                </div>

                <form
                    ref={formRef}
                    className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5"
                    onSubmit={handleSubmit}
                >
                    {error ? <FormErrorAlert message={error} /> : null}

                    <Field
                        label="Company name"
                        required
                        error={fieldError('name', fieldErrors.name)}
                    >
                        <Input
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            onBlur={() => touch('name')}
                            placeholder="Acme Research"
                        />
                    </Field>

                    <Toggle label="Active" checked={isActive} onChange={setIsActive} />
                    <Field
                        as="group"
                        label="Product access"
                        className="space-y-3"
                        legendClassName="mb-2"
                    >
                        <Toggle
                            label="Pulse access"
                            checked={hasPulseAccess}
                            onChange={setHasPulseAccess}
                        />
                        <Toggle
                            label="Pro access"
                            checked={hasProAccess}
                            onChange={setHasProAccess}
                        />
                        <Toggle
                            label="Userlytics access"
                            checked={userlytics.enabled}
                            onChange={(enabled) =>
                                setUserlytics((current) => ({ ...current, enabled }))
                            }
                        />

                        {userlytics.enabled ? (
                            <div className="ml-4 border-l-2 border-neutral-200 pl-4">
                                <UserlyticsFieldGroup
                                    userlytics={userlytics}
                                    missing={userlyticsMissing}
                                    error={visibleUserlyticsError}
                                    onChange={(key, value) =>
                                        setUserlytics((current) => ({ ...current, [key]: value }))
                                    }
                                />
                            </div>
                        ) : null}
                    </Field>

                    <div className={dialogFooterClassName}>
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" loading={isPending} disabled={isPending}>
                            {COMPANY_SUBMIT_LABELS[mode][isPending ? 'pending' : 'idle']}
                        </Button>
                    </div>
                </form>
            </div>
        </ModalOverlay>
    );
}

function Toggle({
    checked,
    label,
    onChange,
}: {
    checked: boolean;
    label: string;
    onChange: (checked: boolean) => void;
}) {
    return (
        <label className={`flex items-center gap-3 ${formFieldGroupSurfaceClassName}`}>
            <input
                type="checkbox"
                checked={checked}
                onChange={(event) => onChange(event.target.checked)}
                className="size-4 rounded border-neutral-300"
            />
            <span className="text-sm font-semibold text-neutral-950">{label}</span>
        </label>
    );
}
